import { useQuery } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import type { NostrMetadata } from '@nostrify/nostrify';
import { profileSearchIndex, type IndexedProfile } from '@/lib/profileSearchIndex';

// Search relays specifically optimized for profile searches
const SEARCH_RELAY_URLS = [
  'wss://relay.nostr.band/',
  'wss://search.nos.today/',
];

export interface ProfileSearchResult {
  pubkey: string;
  metadata: NostrMetadata;
  source: 'local' | 'remote';
}

/**
 * Hook to search for profiles both locally and remotely
 */
export function useProfileSearch(query: string, enabled = true) {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['profile-search', query],
    queryFn: async (c): Promise<ProfileSearchResult[]> => {
      if (!query.trim()) return [];

      // First, search local index (instant)
      const localResults = profileSearchIndex.search(query, 5);
      const results: ProfileSearchResult[] = localResults.map((profile) => ({
        ...profile,
        source: 'local' as const,
      }));

      // If we have enough local results, return them immediately
      // Remote search will run in background for cache warming
      if (results.length >= 3) {
        // Warm cache in background without blocking
        Promise.resolve().then(async () => {
          try {
            const signal = AbortSignal.timeout(1500);
            const searchGroup = nostr.group(SEARCH_RELAY_URLS);
            const events = await searchGroup.query(
              [{ kinds: [0], search: query, limit: 10 }],
              { signal }
            );

            // Add to local index for future searches
            for (const event of events) {
              try {
                const metadata: NostrMetadata = JSON.parse(event.content);
                profileSearchIndex.add(event.pubkey, metadata);
              } catch {
                // Skip invalid metadata
              }
            }
          } catch {
            // Silent fail for background cache warming
          }
        });

        return results;
      }

      // If we need more results, wait for remote search
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(1500)]);

      try {
        const searchGroup = nostr.group(SEARCH_RELAY_URLS);
        const events = await searchGroup.query(
          [
            {
              kinds: [0],
              search: query,
              limit: 10,
            },
          ],
          { signal }
        );

        // Parse and deduplicate results
        const seenPubkeys = new Set(results.map((r) => r.pubkey));

        for (const event of events) {
          if (seenPubkeys.has(event.pubkey)) continue;

          try {
            const metadata: NostrMetadata = JSON.parse(event.content);

            // Add to local index for future searches
            profileSearchIndex.add(event.pubkey, metadata);

            results.push({
              pubkey: event.pubkey,
              metadata,
              source: 'remote',
            });

            seenPubkeys.add(event.pubkey);

            // Stop when we have enough results
            if (results.length >= 5) break;
          } catch {
            // Skip invalid metadata
          }
        }
      } catch (error) {
        // If remote search fails, return local results
        console.error('Remote profile search failed:', error);
      }

      return results;
    },
    enabled: enabled && !!query.trim(),
    staleTime: 60000, // 1 minute
    gcTime: 300000, // Keep in cache for 5 minutes
    placeholderData: (previousData) => previousData, // Show previous results while loading
  });
}

/**
 * Hook to populate the local search index with profiles
 * Should be called when profiles are fetched elsewhere in the app
 */
export function usePopulateSearchIndex() {
  return {
    addProfile: (pubkey: string, metadata: NostrMetadata) => {
      profileSearchIndex.add(pubkey, metadata);
    },
    addProfiles: (profiles: Array<{ pubkey: string; metadata: NostrMetadata }>) => {
      profiles.forEach(({ pubkey, metadata }) => {
        profileSearchIndex.add(pubkey, metadata);
      });
    },
  };
}
